// FollowersFollowingModal.tsx
import React, { useEffect, useState, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { Tr } from '../../../_metronic/i18n/Tr'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { fetchData, getEncryptedRoute, toasterrormsg } from '../../../reusable'
import { Link } from 'react-router-dom'

interface UserItem {
    userId: number
    fullName: string
    profile: string
    username?: string
    bio?: string
    isFollowing?: boolean
}

interface FollowersFollowingModalProps {
    show: boolean
    onHide: () => void
    type: 'followers' | 'following' |""
    userId: number
    baseUrl: string
}

const FollowersFollowingModal: React.FC<FollowersFollowingModalProps> = ({
    show,
    onHide,
    type,
    userId,
    baseUrl
}) => {
    const t = Tr()

    // State management
    const [users, setUsers] = useState<UserItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [limit] = useState(10)
    const [offset, setOffset] = useState(0)
    const [totalRecord, setTotalRecord] = useState(0)
    const [searchTerm, setSearchTerm] = useState('')
    const [sortBy] = useState('created')
    const [sort] = useState('desc')

    // Ref for scroll container and search debounce
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Calculate if there are more records to load
    const hasMore = users.length < totalRecord

    // API call function
    const fetchUsers = async (currentOffset: number, isLoadMore = false, searchValue = searchTerm) => {
        if (isLoadMore) {
            setIsLoadingMore(true)
        } else {
            setIsLoading(true)
        }

        const endpoint = type === 'followers'
            ? 'admin/explore/user/follower/list'
            : 'admin/explore/user/following/list'

        const param = {
            userId: userId,
            limit: limit,
            offset: currentOffset,
            term: searchValue.trim(),
            sort: sort,
            sortBy: sortBy
        }

        try {
            const response = await fetchData(endpoint, param, 'POST')

            if (response.status == '200') {
                const data = response.data

                if (isLoadMore) {
                    setUsers(prev => [...prev, ...data.list])
                } else {
                    setUsers(data.list)
                }

                // Set total record from API response
                setTotalRecord(data.totalRecord || 0)
            } else {
                toasterrormsg(response.message)
                setUsers([])
                setTotalRecord(0)
            }
        } catch (error) {
            console.error('Error fetching users:', error)
            toasterrormsg('Failed to load users')
            setUsers([])
            setTotalRecord(0)
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false)
            } else {
                setIsLoading(false)
            }

        }
    }

    // Reset and fetch when modal opens
    useEffect(() => {
        if (show) {
            // Reset everything
            setUsers([])
            setOffset(0)
            setTotalRecord(0)
            setSearchTerm('')
            setIsLoading(false)
            setIsLoadingMore(false)

            // Fetch initial data
            fetchUsers(0, false, '')
        }
    }, [show, type, userId])

    // Handle search with debounce
    const handleSearch = (value: string) => {
        setSearchTerm(value)

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
        }

        // Set new timeout for debounced search
        searchTimeoutRef.current = setTimeout(() => {
            setUsers([])
            setOffset(0)
            setTotalRecord(0)
            fetchUsers(0, false, value)
        }, 500) // 500ms debounce
    }

    // Handle scroll for infinite loading
    const handleScroll = () => {
        if (!scrollContainerRef.current) return

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current

        // Trigger load more when user scrolls near bottom
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            if (hasMore && !isLoadingMore && !isLoading) {
                const newOffset = offset + limit
                setOffset(newOffset)
                fetchUsers(newOffset, true)
            }
        }
    }

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current)
            }
        }
    }, [])

    // Render loading skeleton
    const renderSkeleton = () => {
        return (
            <>
                {[1, 2, 3].map((item) => (
                    <div key={item} className="user-item">
                        <div className="user-avatar">
                            <Skeleton circle width={50} height={50} />
                        </div>
                        <div className="user-info">
                            <Skeleton width={120} height={16} />
                            <Skeleton width={80} height={12} />
                        </div>
                    </div>
                ))}
            </>
        )
    }

    // Render user list
    const renderUserList = () => {
        if (isLoading) {
            return renderSkeleton()
        }

        if (users.length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-state-icon d-flex justify-content-center mb-5">
                        <KTIcon iconName='people' className='fs-3x text-muted' iconType='solid' />
                    </div>
                    <div className="empty-state-text">
                        {searchTerm
                            ? t('No users found matching your search')
                            : type === 'followers'
                                ? t('No followers yet')
                                : t('Not following anyone yet')
                        }
                    </div>
                </div>
            )
        }

        return (
            <>
                {users.map((user) => (
                    <div key={user.userId} className="user-item">
                        <div className="user-avatar">
                            <img
                                src={user.profile && baseUrl ? baseUrl + user.profile : toAbsoluteUrl('/media/avatars/blank.png')}
                                alt={user.fullName || 'User'}
                                onError={(e) => {
                                    e.currentTarget.onerror = null
                                    e.currentTarget.src = toAbsoluteUrl('/media/avatars/blank.png')
                                }}
                            />
                        </div>
                        <div className="user-info">
                            <Link
                                to={getEncryptedRoute("../explore/user/", { userId: user.userId })}
                                className="user-name text-dark text-hover-primary text-decoration-none"
                                onClick={onHide}
                            >
                                {user.fullName || "N/A"}
                            </Link>
                        </div>
                    </div>
                ))}

                {/* Loading more indicator */}
                {isLoadingMore && (
                    <div className="loading-more">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2 text-muted">{t('Loading more...')}</span>
                    </div>
                )}

                {/* End of list indicator */}
                {!hasMore && users.length > 0 && (
                    <div className="end-of-list text-center text-muted py-3">
                        {t('No more users to load')}
                    </div>
                )}
            </>
        )
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="followers-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center justify-content-between w-100 me-3">
                    <span>{type === 'followers' ? t('Followers') : t('Following')}</span>
                    {totalRecord > 0 && (
                        <span className="badge badge-light-primary fs-7">
                            {totalRecord} {totalRecord === 1 ? t('user') : t('users')}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Search Bar */}
                <div className="search-container mb-4">
                    <div className="position-relative d-flex align-items-center">
                        <KTIcon
                            iconName='magnifier'
                            className='fs-3 position-absolute ms-4  text-gray-500'
                        />
                        <input
                            type='text'
                            className='form-control form-control-solid ps-12'
                            placeholder={`Search ${type === 'followers' ? t('Followers') : t('Following')}`}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="btn btn-sm btn-icon btn-active-light-primary position-absolute end-0 top-50 translate-middle-y me-2"
                                onClick={() => handleSearch('')}
                            >
                                <KTIcon iconName='cross' className='fs-3' />
                            </button>
                        )}
                    </div>
                </div>

                {/* Users List */}
                <div
                    className="users-list-container"
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                >
                    {renderUserList()}
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default FollowersFollowingModal